import React, { useContext, useEffect, useRef, useState } from "react";
import {
  Button,
  CardList,
  Checkbox,
  Filter,
  LoadingSpinner,
  MaxWidthWrapper,
  PaginationControls,
  Popover,
  usePagination,
} from "@panda0/panda0-ui";
import { cn, fetcher } from "@panda0/panda0-utils";
import {
  ArrowDownWideNarrow,
  BriefcaseBusiness,
  CalendarDays,
  Laptop,
  LayoutGrid,
  ListFilter,
  Star,
  TableOfContents,
  Trophy,
  Upload,
  UserIcon,
} from "lucide-react";
import useSWR from "swr";

import { useJobInsightFilters } from "@/ui/jobs/use-job-insight-filters";
import { SearchBoxPersisted } from "@/ui/shared/search-box";
import { CandidatesChart } from "./charts/candidateschart";
import { ShortlistedChart } from "./charts/shortlisted";
import AnalyticsCard from "./insights/analytics-card";
import CandidateCard from "./insights/candidate-card";
import CandidateRow from "./insights/candidate-row";
import CandidateScorecard from "./insights/candidate-scorecard";
import InsightDisplay from "./insights/display";
import InsightsDisplayProvider from "./insights/display-provider";
import {
  useScorecardPDF,
} from "./insights/scorecard-pdf-generator";
import { CandidateListContext, InsightsDisplayContext } from "./insights/types";
import { toast } from "sonner";
import { JobModalContext } from "./types";
import { ScoreDistributionChart } from "./score-distribution";

// Main Job Insights Component
function JobInsights() {
  const scorecardRef = useRef<HTMLDivElement>(null);
  const { generatePDF, isGenerating, error } = useScorecardPDF();
  const [selectedCandidates, setSelectedCandidates] = useState(
    new Set<string>()
  );
  const [isFilterVisible, setIsFilterVisible] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCandidateId, setSelectedCandidateId] = useState(null);
  const [shortlistedCandidates, setShortlistedCandidates] = useState<string[]>(
    []
  );
  const [selectedSortOption, setSelectedSortOption] = useState("score");
  const [sortOrder, setSortOrder] = useState("DESC");
  const [isSortOptionsOpen, setIsSortOptionsOpen] = useState(false);
  const [detailsTab, setDetailsTab] = useState("overview");
  const [openMenuCandidateId, setOpenMenuCandidateId] = useState<string>("");
  const [isStarSelected, setIsStarSelected] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [activeStep, setActiveStep] = useState(0);
  const [candidateDetails, setCandidateDetails] = useState(null);
  const [loadingCandidates, setLoadingCandidates] = useState(true);
  const [loadingError, setLoadingError] = useState<Error>();
  const [showShortlisted, setShowShortlisted] = useState(false);
  const [analyticsData, setAnalyticsData] = useState({
    cards: [],
    sourceAnalytics: [],
    hiringRatioData: [],
    shortlistedAnalytics: [],
  });

  const { pagination, setPagination } = usePagination();

  // Get the jobId from the context
  const { savedJobId, workspaceId } = useContext(JobModalContext);

  const {
    data,
    error: fetchError,
    isLoading,
    mutate,
  } = useSWR<any>(
    savedJobId
      ? `/api/v1/jobs/${savedJobId}/insights?workspaceId=${workspaceId}&page=${pagination.pageIndex}&pageSize=${pagination.pageSize}&sortBy=${selectedSortOption}&sortOrder=${sortOrder}&shortlistedOnly=${showShortlisted ? "true" : "false"}`
      : null,
    fetcher
  );

  useEffect(() => {
    if (data) {
      if (data.candidates) {
        // Extract shortlisted candidates
        const shortlisted = data.candidates
          .filter((c) => c.shortlisted)
          .map((c) => c.id);
        setShortlistedCandidates(shortlisted);
      }

      // Update analytics data
      if (data.analytics) {
        setAnalyticsData({
          cards: data.analytics.cards || [],
          sourceAnalytics: data.analytics.sourceAnalytics || [],
          hiringRatioData: data.analytics.hiringRatioData || [],
          shortlistedAnalytics: data.analytics.shortlistedAnalytics || [],
        });
      }

      // Update pagination
      if (data.pagination) {
        setPagination({
          ...pagination,
        });
      }

      // Update quiz questions
      if (data.quizQuestions) {
        // Store quiz questions for display in candidate details
      }

      setLoadingCandidates(false);
    } else if (fetchError) {
      setLoadingError(fetchError);
      setLoadingCandidates(false);
    }
  }, [data, fetchError, setPagination]);

  const pageRefs = useRef<{ [key: number]: HTMLDivElement | null }>({
    1: null,
    2: null,
    3: null,
  });

  // Handle shortlist action
  const toggleShortlist = async (candidateId) => {
    const isCurrentlyShortlisted = shortlistedCandidates.includes(candidateId);

    try {
      // Call API to update shortlist status
      const response = await fetch(
        `/api/v1/jobs/${savedJobId}/candidates/${candidateId}/shortlist?workspaceId=${workspaceId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            shortlist: !isCurrentlyShortlisted,
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to update shortlist status");
      }

      // Update local state
      if (isCurrentlyShortlisted) {
        setShortlistedCandidates(
          shortlistedCandidates.filter((id) => id !== candidateId)
        );
      } else {
        setShortlistedCandidates([...shortlistedCandidates, candidateId]);
      }

      // Refresh data
      mutate();
    } catch (error) {
      console.error("Error updating shortlist status:", error);
      // Handle error (e.g., show toast notification)
    }
  };

  // Handle bulk shortlist
  const handleShortlist = async () => {
    if (selectedCandidates.size === 0) return;

    try {
      // Call API to update shortlist status for all selected candidates
      const response = await fetch(
        `/api/v1/jobs/${savedJobId}/shortlist-bulk?workspaceId=${workspaceId}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            candidateIds: Array.from(selectedCandidates),
            action: "shortlist",
          }),
        }
      );

      if (!response.ok) {
        throw new Error("Failed to shortlist candidates");
      }

      // Update local state
      const result = await response.json();
      setShortlistedCandidates([
        ...shortlistedCandidates,
        ...result.processed.filter((id) => !shortlistedCandidates.includes(id)),
      ]);

      // Clear selections after shortlisting
      setSelectedCandidates(new Set());

      // Refresh data
      mutate();
    } catch (error) {
      console.error("Error shortlisting candidates:", error);
      // Handle error (e.g., show toast notification)
    }
  };

  // Handle opening candidate modal
  const openCandidateModal = async (candidateId) => {
    setSelectedCandidateId(candidateId);
    setDetailsTab("overview"); // Reset to overview tab

    try {
      setIsModalOpen(true); // Open modal first to show loading state

      // Fetch candidate details
      const response = await fetch(
        `/api/v1/jobs/${savedJobId}/candidates/${candidateId}?workspaceId=${workspaceId}`
      );

      if (!response.ok) {
        throw new Error("Failed to fetch candidate details");
      }

      const candidateData = await response.json();

      // Store candidate data for display in modal
      setCandidateDetails(candidateData);
    } catch (error) {
      console.error("Error fetching candidate details:", error);
      toast.error("Failed to load candidate details");
      setIsModalOpen(false);
    }
  };

  // Handle sort selection
  const handleSortSelection = (value) => {
    setSelectedSortOption(value);
    setIsSortOptionsOpen(false);
  };

  // Handle page change in scorecard view
  const handlePageChange = (direction: number) => {
    setCurrentPage((prevPage) => {
      const newPage = prevPage + direction;
      return newPage >= 1 && newPage <= 3 ? newPage : prevPage;
    });
  };

  // Toggle star status
  const handleStarClick = () => {
    setIsStarSelected(!isStarSelected);
  };

  // Function to download PDF of the scorecard
  const downloadPDF = async () => {
    if (!selectedCandidateId) return;

    const candidate =
      candidateDetails ||
      data?.candidates.find((c) => c.id === selectedCandidateId);
    if (!candidate) {
      toast.error("Candidate details not found");
      return;
    }

    await generatePDF(candidate, shortlistedCandidates, scorecardRef);
  };

  // Function to filter displayed candidates based on shortlist setting
  const getFilteredCandidates = () => {
    const { showShortlisted } = useContext(InsightsDisplayContext);
    if (!showShortlisted) return data.candidates;

    return data.candidates.filter(
      (candidate) =>
        shortlistedCandidates.includes(candidate.id) || candidate.shortlisted
    );
  };

  // Get selected candidate by ID
  const getSelectedCandidate = () => {
    return (
      data.candidates.find((c) => c.id === selectedCandidateId) || data.candidates[0]
    );
  };


  const sortOptions = [
    { category: "Candidate Job Fit", values: ["High to Low", "Low to High"] },
    { category: "Experience Level", values: ["High to Low", "Low to High"] },
    { category: "Ratings", values: ["High to Low", "Low to High"] },
  ];

  const {
    filters,
    activeFilters,
    onSelect,
    onRemove,
    onRemoveAll,
    setSearch,
    setSelectedFilter,
  } = useJobInsightFilters();

  return (
    <InsightsDisplayProvider>
      <CandidateListContext.Provider
        value={{
          selectedCandidates,
          setSelectedCandidates,
          openMenuCandidateId,
          setOpenMenuCandidateId,
          shortlistedCandidates,
          setShortlistedCandidates,
        }}
      >
        <div className="flex flex-col w-full h-full">
          {loadingCandidates ? (
            <div className="flex items-center justify-center h-64">
              <LoadingSpinner />
            </div>
          ) : loadingError ? (
            <div className="text-center p-8 text-red-600">
              <p>Error loading insights data: {loadingError?.message}</p>
              <button
                onClick={() => mutate()}
                className="mt-4 px-4 py-2 bg-neutral-800 text-white rounded hover:bg-neutral-700"
              >
                Retry
              </button>
            </div>
          ) : (
            // Content with analytics cards
            <div className="space-y-6">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 w-full gap-4">
                {analyticsData.cards.map((card, index) => (
                  <AnalyticsCard key={index} cardHeader={undefined} cardDescription={undefined} />
                ))}
              </div>

              {/* Charts section */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <CardList.Card outerClassName="border rounded-2xl shadow-md bg-white h-[300px] flex items-center justify-center">
                  <div>
                    <p className="font-medium">Score Graph</p>
                    <div>
                      <ScoreDistributionChart />
                    </div>
                  </div>
                </CardList.Card>

                <CardList.Card outerClassName="border rounded-2xl shadow-md bg-white h-[300px] flex items-center justify-center">
                  <div>
                    <p className="font-medium">Candidates</p>
                    <div>
                      <CandidatesChart data={analyticsData.sourceAnalytics} />
                    </div>
                  </div>
                </CardList.Card>

                <CardList.Card outerClassName="border rounded-2xl shadow-md bg-white h-[300px] flex items-center justify-center">
                  <div>
                    <p className="font-medium">Shortlisted</p>
                    <div>
                      <ShortlistedChart
                        data={analyticsData.shortlistedAnalytics}
                      />
                    </div>
                  </div>
                </CardList.Card>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-2 gap-4 w-full items-center">
                <div className="flex flex-wrap items-center gap-3 w-full">
                  {/* Selected Count */}
                  <div className="w-full sm:w-48 md:w-36 lg:w-[85px]">
                    <h4 className="text-sm font-medium text-neutral-500">
                      Selected: {selectedCandidates.size}
                    </h4>
                  </div>

                  {/* Insight Display */}
                  <div className="w-full sm:w-48 md:w-36 lg:w-40">
                    <InsightDisplay />
                  </div>

                  {/* Filter Select */}
                  <div className="w-full sm:w-48 md:w-36 lg:w-40">
                    <Filter.Select
                      filters={filters}
                      activeFilters={activeFilters}
                      onRemove={onRemove}
                      onSelect={onSelect}
                      className="w-full"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
                  <div className="w-40 sm:w-40 lg:w-40">
                    <SearchBoxPersisted inputClassName="h-10 w-full" />
                  </div>
                  <div className="w-32 sm:w-32 lg:w-32">
                    <Button
                      text="Shortlist"
                      shortcut="S"
                      onClick={handleShortlist}
                      className="h-10 px-4"
                    />
                  </div>
                  <div className="relative">
                    <Popover
                      content={
                        <div className="w-80 sm:w-60 absolute right-0 top-full mt-2 border border-sky-100 bg-white p-4 shadow-lg">
                          {sortOptions.map((option, index) => (
                            <div
                              key={index}
                              className="px-4 py-2 border-b text-gray-700"
                            >
                              <p className="font-bold">{option.category}</p>
                              {option.values.map((value, i) => (
                                <div
                                  key={i}
                                  className="flex items-center cursor-pointer hover:bg-gray-100 px-2 py-1 rounded-md gap-x-2"
                                  onClick={() => handleSortSelection(value)}
                                >
                                  <Checkbox
                                    checked={selectedSortOption === value}
                                    onCheckedChange={() =>
                                      handleSortSelection(value)
                                    }
                                  />
                                  {value}
                                </div>
                              ))}
                            </div>
                          ))}
                          <Button
                            text="Star"
                            variant="outline"
                            icon={<Star className="h-4 w-4" />}
                            shortcut="S"
                            className="h-9 px-2 font-medium"
                          />

                          <Button
                            text="Upload"
                            variant="outline"
                            icon={<Upload className="h-4 w-4" />}
                            shortcut="U"
                            className="h-9 px-2 font-medium"
                          />
                        </div>
                      }
                      openPopover={isSortOptionsOpen}
                      setOpenPopover={setIsSortOptionsOpen}
                    >
                      <Button
                        variant="outline"
                        className={cn(
                          "h-10 w-10 px-1.5 transition-all duration-200 border border-gray-300"
                        )}
                        icon={
                          <ArrowDownWideNarrow className="h-5 w-5 shrink-0" />
                        }
                        onClick={() => setIsSortOptionsOpen(!isSortOptionsOpen)}
                      />
                    </Popover>
                  </div>
                </div>
              </div>

              {/* Candidates view sections */}
              <MaxWidthWrapper className="grid gap-y-2 max-w-screen-2xl px-0 lg:px-0">
                <InsightsDisplayContext.Consumer>
                  {({ viewMode, displayProperties, showShortlisted }) => {
                    const filteredCandidates = showShortlisted
                      ? data.candidates.filter(
                        (candidate) =>
                          shortlistedCandidates.includes(candidate.id) ||
                          candidate.shortlisted
                      )
                      : data.candidates;

                    return (
                      <>
                        {/* Cards View */}
                        {viewMode === "cards" ? (
                          <CardList variant="loose">
                            {filteredCandidates.map((candidate) => (
                              <CandidateCard
                                key={candidate.id}
                                candidate={candidate}
                                openCandidateModal={openCandidateModal}
                                compact={false}
                              />
                            ))}
                          </CardList>
                        ) : (
                          /* Table View */
                          <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                              <thead>
                                <tr className="bg-neutral-50">
                                  {displayProperties.includes("rank") && (
                                    <th className="p-3 text-left">
                                      <div className="flex items-center gap-1">
                                        <ListFilter className="h-4 w-4" />
                                        <span>Rank</span>
                                      </div>
                                    </th>
                                  )}
                                  {displayProperties.includes("candidateid") && (
                                    <th className="p-3 text-left w-[12%]">
                                      <div className="flex items-center gap-1">
                                        <LayoutGrid className="h-4 w-4" />
                                        <span>CandidateID</span>
                                      </div>
                                    </th>
                                  )}
                                  {displayProperties.includes("jobFit") && (
                                    <th className="p-3 text-left">
                                      <div className="flex items-center gap-1">
                                        <BriefcaseBusiness className="h-4 w-4" />
                                        <span>Job Fit%</span>
                                      </div>
                                    </th>
                                  )}
                                  {displayProperties.includes("name") && (
                                    <th className="p-3 text-left">
                                      <div className="flex items-center gap-1">
                                        <UserIcon className="h-4 w-4" />
                                        <span>Name</span>
                                      </div>
                                    </th>
                                  )}
                                  {displayProperties.includes("score") && (
                                    <th className="p-3 text-left">
                                      <div className="flex items-center gap-1">
                                        <Trophy className="h-4 w-4" />
                                        <span>Score/100</span>
                                      </div>
                                    </th>
                                  )}
                                  {displayProperties.includes("source") && (
                                    <th className="p-3 text-left">
                                      <div className="flex items-center gap-1">
                                        <Laptop className="h-4 w-4" />
                                        <span>Source</span>
                                      </div>
                                    </th>
                                  )}
                                  {displayProperties.includes("date") && (
                                    <th className="p-3 text-left">
                                      <div className="flex items-center gap-1">
                                        <CalendarDays className="h-4 w-4" />
                                        <span>Date</span>
                                      </div>
                                    </th>
                                  )}
                                  {displayProperties.includes("ratings") && (
                                    <th className="p-3 text-left">
                                      <div className="flex items-center gap-1">
                                        <Star className="h-4 w-4" />
                                        <span>Ratings</span>
                                      </div>
                                    </th>
                                  )}
                                  {displayProperties.includes("actions") && (
                                    <th className="p-3 text-left">
                                      <div className="flex items-center gap-1">
                                        <TableOfContents className="h-4 w-4" />
                                        <span>Actions</span>
                                      </div>
                                    </th>
                                  )}
                                </tr>
                              </thead>
                              <tbody>
                                {filteredCandidates.map((candidate) => (
                                  <CandidateRow
                                    key={candidate.id}
                                    candidate={candidate}
                                    openCandidateModal={openCandidateModal}
                                  />
                                ))}
                              </tbody>
                            </table>
                          </div>
                        )}
                      </>
                    );
                  }}
                </InsightsDisplayContext.Consumer>
              </MaxWidthWrapper>

              {/* Pagination */}
              <div className="h-[90px]" />
              <div className="fixed bottom-4 left-0 w-full sm:max-[1330px]:w-[calc(100%-150px)] md:left-[240px] md:w-[calc(100%-240px)] md:max-[1330px]:w-[calc(100%-240px-150px)]">
                <div className="relative left-1/2 w-full max-w-[768px] -translate-x-1/2 px-5 max-[1330px]:left-0 max-[1330px]:translate-x-0">
                  <div className="rounded-xl border border-neutral-200 bg-white px-4 py-3.5 [filter:drop-shadow(0_5px_8px_#222A351d)]">
                    <PaginationControls
                      pagination={pagination}
                      setPagination={setPagination}
                      totalCount={data.candidates.length}
                      unit={(plural) => `${plural ? "candidates" : "candidate"}`}
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Candidate details modal*/}
          {isModalOpen && selectedCandidateId && (
            <CandidateScorecard
              candidate={candidateDetails || {}}
              candidates={candidateDetails ? [candidateDetails] : []}
              isOpen={isModalOpen}
              setOpen={setIsModalOpen}
              onClose={() => setIsModalOpen(false)}
              shortlistedCandidates={shortlistedCandidates}
              toggleShortlist={toggleShortlist}
              downloadPDF={downloadPDF}
              linkedInData={{}}
              userData={{}}
              heatMapValue={[]}
              // linkedInData={candidateDetails?.linkedInData || {}}
              // userData={candidateDetails?.githubData?.userData || {}}
              // heatMapValue={candidateDetails?.githubData?.heatMapValue || []}
              quizQuestions={data?.quizQuestions || { questions: [] }}
              scorecardRef={scorecardRef}
            />
          )}
        </div>
      </CandidateListContext.Provider>
    </InsightsDisplayProvider>
  );
}

export default React.memo(JobInsights);
